import { Link, NavLink } from "react-router-dom";
import { useEffect, useState } from "react";
import { Search, Menu, X } from "lucide-react";
import api from "../lib/api";

export default function Header() {
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    api.get("/categories").then(r => setCategories(r.data || [])).catch(() => {});
    const t = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(t);
  }, []);

  const dateStr = now.toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long", year: "numeric" });

  const onSubmit = (e) => {
    e.preventDefault();
    if (q.trim()) {
      window.location.href = `/busca?q=${encodeURIComponent(q.trim())}`;
    }
  };

  return (
    <header className="bg-white border-b-2 border-black sticky top-0 z-50" data-testid="site-header">
      {/* top bar */}
      <div className="border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between text-xs font-sans-ui text-zinc-500">
          <span className="capitalize" data-testid="header-date">{dateStr}</span>
          <div className="hidden md:flex items-center gap-4">
            <Link to="/sobre" className="hover:text-black">Sobre</Link>
            <Link to="/contato" className="hover:text-black">Contato</Link>
            <Link to="/admin/login" className="hover:text-black" data-testid="header-admin-link">Acesso Admin</Link>
          </div>
        </div>
      </div>

      {/* logo bar */}
      <div className="max-w-7xl mx-auto px-4 py-6 flex flex-col items-center text-center">
        <Link to="/" className="block" data-testid="site-logo">
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-none">
            Chiquinho <span className="italic font-normal">o 01 do Acre</span>
          </h1>
          <p className="overline mt-2">Edição digital · Jornalismo do Acre</p>
        </Link>
      </div>

      {/* nav */}
      <nav className="border-t border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-between">
          <div className="hidden md:flex items-center gap-1 overflow-x-auto">
            <NavLink to="/" end className={({isActive}) => `font-sans-ui text-xs uppercase tracking-widest font-semibold px-3 py-3 hover:text-red-700 ${isActive ? 'text-red-700' : 'text-zinc-800'}`} data-testid="nav-home">Início</NavLink>
            <NavLink to="/noticias" className={({isActive}) => `font-sans-ui text-xs uppercase tracking-widest font-semibold px-3 py-3 hover:text-red-700 ${isActive ? 'text-red-700' : 'text-zinc-800'}`} data-testid="nav-news">Notícias</NavLink>
            <NavLink to="/destaques" className={({isActive}) => `font-sans-ui text-xs uppercase tracking-widest font-semibold px-3 py-3 hover:text-red-700 ${isActive ? 'text-red-700' : 'text-zinc-800'}`} data-testid="nav-featured">Destaques</NavLink>
            <NavLink to="/ultimas" className={({isActive}) => `font-sans-ui text-xs uppercase tracking-widest font-semibold px-3 py-3 hover:text-red-700 ${isActive ? 'text-red-700' : 'text-zinc-800'}`} data-testid="nav-latest">Últimas</NavLink>
            <NavLink to="/populares" className={({isActive}) => `font-sans-ui text-xs uppercase tracking-widest font-semibold px-3 py-3 hover:text-red-700 ${isActive ? 'text-red-700' : 'text-zinc-800'}`} data-testid="nav-popular">Populares</NavLink>
            <span className="text-zinc-300">|</span>
            {categories.slice(0, 6).map(c => (
              <NavLink key={c.id} to={`/categoria/${c.slug}`} className={({isActive}) => `font-sans-ui text-xs uppercase tracking-widest font-semibold px-3 py-3 hover:text-red-700 ${isActive ? 'text-red-700' : 'text-zinc-800'}`} data-testid={`nav-cat-${c.slug}`}>{c.name}</NavLink>
            ))}
          </div>

          <button className="md:hidden p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>

          <form onSubmit={onSubmit} className="hidden md:flex items-center gap-2 py-2" data-testid="search-form">
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar notícias..."
              className="bg-zinc-50 border border-zinc-300 px-3 py-1.5 text-sm font-sans-ui focus:outline-none focus:border-black"
              data-testid="search-input"
            />
            <button type="submit" className="p-2 border border-zinc-300 hover:bg-zinc-100" data-testid="search-submit">
              <Search size={14} />
            </button>
          </form>
        </div>

        {open && (
          <div className="md:hidden border-t border-zinc-200 px-4 py-2 bg-white" data-testid="mobile-menu">
            <form onSubmit={onSubmit} className="flex items-center gap-2 py-2">
              <input
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Buscar..."
                className="flex-1 bg-zinc-50 border border-zinc-300 px-3 py-1.5 text-sm font-sans-ui"
              />
              <button type="submit" className="p-2 border border-zinc-300"><Search size={14} /></button>
            </form>
            <div className="flex flex-col">
              {[
                {to: "/", label: "Início"},
                {to: "/noticias", label: "Notícias"},
                {to: "/destaques", label: "Destaques"},
                {to: "/ultimas", label: "Últimas"},
                {to: "/populares", label: "Populares"},
                ...categories.map(c => ({to: `/categoria/${c.slug}`, label: c.name})),
                {to: "/sobre", label: "Sobre"},
                {to: "/contato", label: "Contato"},
              ].map((it, i) => (
                <NavLink key={i} to={it.to} onClick={() => setOpen(false)} className="font-sans-ui text-sm uppercase tracking-widest font-semibold py-2 border-b border-zinc-100">{it.label}</NavLink>
              ))}
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
